<!--
  @project E-School Web
  @author Vothana CHY
  @date 2026-07-13
-->

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import imgLogo from '~/assets/images/logo-full.png';
import logoIcon from '~/assets/icons/logo.svg?url';
import iconKh from '~/assets/icons/kh.svg?url';
import iconEn from '~/assets/icons/en.svg?url';

const { locale, setLocale, t } = useI18n();

const currentLangFlag = computed(() => {
  return locale.value === 'kh' ? iconKh : iconEn;
});

const toggleLocale = () => {
  setLocale(locale.value === 'kh' ? 'en' : 'kh');
};

const navLinks = computed(() => [
  { label: t('header.about'), href: '/#about', dropdown: false },
  { label: t('header.quality'), href: '/#quality', dropdown: false },
  { label: t('header.video'), href: '/#video', dropdown: false },
  { label: t('header.zoom'), href: '/courses', dropdown: false },
  { label: t('header.contact'), href: '/e-contact', dropdown: false },
]);

const isCondensed = ref(false);
const headerTranslateY = ref(0);
const topbarHidden = ref(false);
const isMobileMenuOpen = ref(false);

watch(isMobileMenuOpen, (value) => {
  if (!import.meta.client) return;
  if (value) {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.overflowY = 'hidden';
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
  } else {
    document.documentElement.style.overflowY = '';
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }
});
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1200);

// True when screen < 1140px AND topbar is hidden → compact nav mode
const isNavCompact = computed(() => windowWidth.value < 1140 && topbarHidden.value);

const handleScroll = () => {
  if (window.innerWidth < 768) {
    // On mobile the header is position:fixed at top:0.
    // The smart banner (also fixed) sits above it via its own CSS — no extra offset needed.
    headerTranslateY.value = 0;
    isCondensed.value = false;
    return;
  }

  const aboutEl = document.getElementById('about');
  if (!aboutEl) {
    // Fallback if about section is not present (e.g. /courses page)
    // Collapse only the topbar after 100px; keep headerTranslateY=0 so the main nav stays visible
    const scroll = window.scrollY;
    topbarHidden.value = scroll >= 100;
    headerTranslateY.value = 0;
    isCondensed.value = topbarHidden.value;
    return;
  }

  const rect = aboutEl.getBoundingClientRect();
  // We want the header to translate up by:
  // - 0px when rect.top >= 124px
  // - -72px when rect.top <= 52px
  // - dynamically in-between
  if (rect.top >= 124) {
    headerTranslateY.value = 0;
    isCondensed.value = false;
  } else if (rect.top <= 52) {
    headerTranslateY.value = -72;
    isCondensed.value = true;
  } else {
    headerTranslateY.value = -(124 - rect.top);
    isCondensed.value = rect.top < 88; // halfway transition
  }
};

const handleResize = () => {
  windowWidth.value = window.innerWidth;
  handleScroll();
};

onMounted(() => {
  windowWidth.value = window.innerWidth;
  window.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('resize', handleResize, { passive: true });

  handleScroll();
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
  window.removeEventListener('resize', handleResize);
  // Always clean up scroll lock on unmount
  if (import.meta.client) {
    document.documentElement.style.overflowY = '';
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }
});
</script>

<template>
  <header
    class="fixed right-0 left-0 z-50 w-full bg-white shadow-xs transition-[top] duration-75 ease-out"
    :style="{ top: `${headerTranslateY}px` }"
  >
    <!-- ── Topbar (white bg, 72px tall) ── -->
    <div
      class="w-full overflow-hidden bg-white transition-all duration-300 ease-in-out"
      :class="topbarHidden ? 'max-h-0 opacity-0' : 'max-h-[72px] opacity-100'"
    >
      <div class="mx-auto flex h-18 max-w-360 items-center justify-between px-6 md:px-20">
        <!-- Logo -->
        <NuxtLink to="/" class="flex h-17 w-60 shrink-0 items-center overflow-hidden">
          <img :src="imgLogo" alt="E-SCHOOL Cambodia" class="h-full w-full object-contain object-left" />
        </NuxtLink>

        <!-- Right: Actions/Hamburger -->
        <div class="flex items-center gap-3">
          <!-- Desktop/Tablet Actions -->
          <div class="hidden items-center gap-3 md:flex">
            <!-- Download App -->
            <NuxtLink
              to="/download"
              class="flex items-center gap-2 rounded-full border border-brand-blue/20 bg-brand-blue/5 px-5 py-1.75 text-sm font-bold text-brand-blue shadow-[inset_0_1.5px_3px_rgba(4,81,116,0.12)] transition-all duration-150 hover:bg-brand-blue hover:text-white hover:shadow-none active:scale-95"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 4v11"
                />
              </svg>
              {{ t('header.downloadApp') }}
            </NuxtLink>

            <!-- Language Switcher Button -->
            <button
              class="relative flex h-8.5 w-18 cursor-pointer items-center justify-between overflow-visible rounded-full border border-gray-200 bg-gray-100 px-3.5 text-[11px] font-extrabold text-gray-400/80 shadow-[inset_0_1.5px_3px_rgba(0,0,0,0.08)] transition-all duration-150 select-none active:scale-95 active:shadow-[inset_0_2px_5px_rgba(0,0,0,0.12)]"
              title="Switch Language"
              @click="toggleLocale"
            >
              <span class="transition-colors duration-300" :class="locale === 'kh' ? 'opacity-0' : 'opacity-100'"
                >KH</span
              >
              <span class="transition-colors duration-300" :class="locale === 'en' ? 'opacity-0' : 'opacity-100'"
                >EN</span
              >
              <div
                class="absolute top-[3px] left-[3px] h-7 w-7 rounded-full border border-black/5 bg-white shadow-[0_2.5px_6px_rgba(0,0,0,0.24)] transition-transform duration-300 ease-out"
                :style="{ transform: locale === 'kh' ? 'translateX(0)' : 'translateX(38px)' }"
              >
                <Transition name="lang-flip" mode="out-in">
                  <img
                    :key="locale"
                    :src="currentLangFlag"
                    class="h-full w-full object-cover"
                    style="clip-path: circle(50%)"
                    alt=""
                  />
                </Transition>
              </div>
            </button>
          </div>

          <!-- Mobile Hamburger Trigger -->
          <button
            class="flex h-10 w-10 flex-col items-center justify-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 text-gray-700 transition hover:bg-gray-100 md:hidden"
            aria-label="Open menu"
            @click="isMobileMenuOpen = true"
          >
            <span class="h-0.5 w-5 rounded-sm bg-gray-700"></span>
            <span class="h-0.5 w-5 rounded-sm bg-gray-700"></span>
            <span class="h-0.5 w-5 rounded-sm bg-gray-700"></span>
          </button>
        </div>
      </div>
    </div>

    <!-- ── Main Nav Bar (teal, 52px tall) ── -->
    <nav class="hidden w-full bg-brand-blue md:block">
      <div class="mx-auto flex h-13 max-w-360 items-center justify-between px-6 md:px-20">
        <!-- Left: Logo (transitions in when condensed) -->
        <div
          class="flex shrink-0 items-center self-stretch transition-all duration-300"
          :class="isCondensed ? 'opacity-100' : 'pointer-events-none w-0 opacity-0'"
          :style="isCondensed ? (isNavCompact ? 'width:8.75rem' : 'width:13.75rem') : ''"
        >
          <!-- Full logo (≥1140px) -->
          <NuxtLink
            v-if="!isNavCompact"
            to="/"
            class="flex h-9 w-[8.5rem] items-center justify-center rounded-lg bg-white px-1 shadow-xs"
          >
            <img :src="imgLogo" alt="E-SCHOOL Cambodia" class="h-8 w-auto max-w-full object-contain" />
          </NuxtLink>

          <!-- Icon-only logo (<1140px, desktop/tablet only, not mobile) -->
          <NuxtLink
            v-else
            to="/"
            class="hidden h-9 w-9 items-center justify-center rounded-lg bg-white shadow-xs md:flex"
          >
            <img :src="logoIcon" alt="E-SCHOOL" class="h-7 w-7 object-contain" />
          </NuxtLink>
        </div>

        <!-- Middle: Nav Links (centered, scrollable on mobile, hides on mobile when condensed) -->
        <div
          class="no-scrollbar flex flex-1 items-center justify-center overflow-x-auto scroll-smooth whitespace-nowrap transition-all duration-300"
          :class="[isCondensed ? 'hidden gap-6 md:flex lg:gap-10' : '-my-2 flex gap-8 py-2 md:gap-14']"
        >
          <NuxtLink
            v-for="link in navLinks"
            :key="link.label"
            :to="link.href"
            class="flex items-center gap-1 text-[13px] font-semibold tracking-[0.08em] text-white uppercase transition hover:text-white/70"
          >
            {{ link.label }}
            <svg
              v-if="link.dropdown"
              xmlns="http://www.w3.org/2000/svg"
              class="h-3 w-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="3"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </NuxtLink>
        </div>

        <!-- Right: Actions / Mobile Hamburger (transitions in when condensed) -->
        <div
          class="flex shrink-0 items-center justify-end self-stretch transition-all duration-300"
          :class="[
            isCondensed
              ? isNavCompact
                ? 'w-35 gap-2 opacity-100 md:w-35 md:gap-3'
                : 'w-35 gap-2 opacity-100 sm:w-40 md:w-55 md:gap-3'
              : 'pointer-events-none w-0 opacity-0',
          ]"
        >
          <!-- Desktop/Tablet condensed actions -->
          <div class="hidden items-center gap-2 md:flex md:gap-3">
            <!-- Download App Button (Compact version for nav) -->
            <NuxtLink
              to="/download"
              :title="isNavCompact ? t('header.downloadApp') : ''"
              class="flex shrink-0 items-center gap-1.5 rounded-full border border-white/15 bg-black/15 text-[11px] font-bold tracking-wider whitespace-nowrap text-white uppercase shadow-[inset_0_1.5px_3px_rgba(0,0,0,0.2)] transition hover:bg-white hover:text-brand-blue active:scale-95"
              :class="isNavCompact ? 'h-8 w-8 justify-center px-0 py-0' : 'px-3.5 py-1.25'"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-3.5 w-3.5 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 4v11"
                />
              </svg>
              <span v-if="!isNavCompact" class="whitespace-nowrap">{{ t('header.downloadApp') }}</span>
            </NuxtLink>

            <!-- Language Switcher Button (Compact version for nav) -->
            <button
              class="relative flex h-8 w-16 cursor-pointer items-center justify-between overflow-visible rounded-full border border-white/10 bg-black/15 px-3.5 text-[10px] font-extrabold tracking-wider text-white/40 uppercase shadow-[inset_0_1.5px_3px_rgba(0,0,0,0.15)] transition select-none hover:bg-black/25 active:scale-95"
              title="Switch Language"
              @click="toggleLocale"
            >
              <span class="transition-colors duration-300" :class="locale === 'kh' ? 'opacity-0' : 'opacity-100'"
                >KH</span
              >
              <span class="transition-colors duration-300" :class="locale === 'en' ? 'opacity-0' : 'opacity-100'"
                >EN</span
              >
              <div
                class="absolute top-[4px] left-[4px] h-6 w-6 rounded-full border border-black/5 bg-white shadow-[0_2.5px_6px_rgba(0,0,0,0.24)] transition-transform duration-300 ease-out"
                :style="{ transform: locale === 'kh' ? 'translateX(0)' : 'translateX(32px)' }"
              >
                <Transition name="lang-flip" mode="out-in">
                  <img
                    :key="locale"
                    :src="currentLangFlag"
                    class="h-full w-full object-cover"
                    style="clip-path: circle(50%)"
                    alt=""
                  />
                </Transition>
              </div>
            </button>
          </div>

          <!-- Mobile condensed Hamburger Trigger -->
          <button
            class="flex h-9 w-9 flex-col items-center justify-center gap-1 rounded-full border border-white/20 text-white transition hover:bg-white/10 md:hidden"
            aria-label="Open menu"
            @click="isMobileMenuOpen = true"
          >
            <span class="h-0.5 w-4.5 rounded-sm bg-white"></span>
            <span class="h-0.5 w-4.5 rounded-sm bg-white"></span>
            <span class="h-0.5 w-4.5 rounded-sm bg-white"></span>
          </button>
        </div>
      </div>
    </nav>
  </header>

  <!-- ── Mobile Menu Drawer (USlideover) ── -->
  <USlideover
    v-model:open="isMobileMenuOpen"
    side="right"
    :ui="{
      overlay: 'bg-black/60 backdrop-blur-sm',
      content: 'bg-brand-blue text-white w-70 sm:w-[320px] flex flex-col justify-between p-0 ring-0 shadow-2xl',
    }"
  >
    <template #content>
      <div class="flex h-full flex-col justify-between p-6">
        <div>
          <!-- Close Button & Header -->
          <div class="flex items-center justify-between border-b border-white/10 pb-6">
            <img :src="imgLogo" alt="E-SCHOOL" class="h-8 w-auto rounded bg-white object-contain px-2 py-0.5" />
            <button
              class="flex h-8 w-8 items-center justify-center rounded-full border border-white/20 hover:bg-white/10"
              aria-label="Close menu"
              @click="isMobileMenuOpen = false"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Vertically stacked links -->
          <div class="mt-8 flex flex-col gap-6">
            <NuxtLink
              v-for="link in navLinks"
              :key="link.label"
              :to="link.href"
              class="flex items-center justify-between text-base font-medium tracking-wide hover:text-white/80"
              @click="isMobileMenuOpen = false"
            >
              <span>{{ link.label }}</span>
              <svg
                v-if="link.dropdown"
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 opacity-60"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </NuxtLink>
          </div>
        </div>

        <!-- Bottom Actions -->
        <div class="flex flex-col gap-4 border-t border-white/10 pt-6">
          <!-- Download Button -->
          <NuxtLink
            to="/download"
            class="flex items-center justify-center gap-2 rounded-full bg-white py-3 text-sm font-bold text-brand-blue shadow-[0_3px_8px_rgba(0,0,0,0.12),inset_0_1.5px_3px_rgba(255,255,255,0.7)] transition-all duration-150 active:scale-98"
            @click="isMobileMenuOpen = false"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 4v11"
              />
            </svg>
            {{ t('header.downloadApp') }}
          </NuxtLink>

          <!-- Language Switcher -->
          <button
            class="relative mx-auto flex h-11 w-32 cursor-pointer items-center justify-between overflow-visible rounded-full border border-white/20 bg-white/10 px-5 text-[11px] font-extrabold tracking-wider text-white/50 uppercase transition select-none hover:bg-white/15 active:scale-98"
            title="Switch Language"
            @click="toggleLocale"
          >
            <span class="transition-colors duration-300" :class="locale === 'kh' ? 'opacity-0' : 'opacity-100'"
              >KH</span
            >
            <span class="transition-colors duration-300" :class="locale === 'en' ? 'opacity-0' : 'opacity-100'"
              >EN</span
            >
            <div
              class="absolute top-[6px] left-[6px] h-8 w-8 rounded-full border border-black/5 bg-white shadow-[0_2.5px_6px_rgba(0,0,0,0.24)] transition-transform duration-300 ease-out"
              :style="{ transform: locale === 'kh' ? 'translateX(0)' : 'translateX(84px)' }"
            >
              <Transition name="lang-flip" mode="out-in">
                <img
                  :key="locale"
                  :src="currentLangFlag"
                  class="h-full w-full object-cover"
                  style="clip-path: circle(50%)"
                  alt=""
                />
              </Transition>
            </div>
          </button>
        </div>
      </div>
    </template>
  </USlideover>
</template>

<style scoped>
/* Hide scrollbar for Chrome, Safari and Opera */
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
/* Hide scrollbar for IE, Edge and Firefox */
.no-scrollbar {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

/* Flag 3D Flip Transition */
.lang-flip-enter-active,
.lang-flip-leave-active {
  transition:
    transform 0.25s ease,
    opacity 0.25s ease;
}
.lang-flip-enter-from {
  transform: rotateY(90deg);
  opacity: 0;
}
.lang-flip-leave-to {
  transform: rotateY(-90deg);
  opacity: 0;
}
</style>
